import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Logo from "./Logo";

const LINKS = [
    { label: "ACASĂ", to: "/", home: true },
    { label: "CAROTARE", to: "/carotare" },
    { label: "DEMOLĂRI / TĂIERI", to: "/demolari-taiere" },
    { label: "ȘLEFUIRE", to: "/slefuire" },
    { label: "REPASTILARE CAROTE", to: "/repastilare-carote" },
    { label: "CONTACT", to: "/contact" },
];

const scrollToTop = () => {
    if (window.__lenis) window.__lenis.scrollTo(0, { duration: 1.2 });
    else window.scrollTo({ top: 0, behavior: "smooth" });
};

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 32);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        if (open) {
            window.__lenis && window.__lenis.stop();
            document.body.style.overflow = "hidden";
        } else {
            window.__lenis && window.__lenis.start();
            document.body.style.overflow = "";
        }
        return () => {
            window.__lenis && window.__lenis.start();
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <>
            <header
                data-testid="site-header"
                className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                    scrolled
                        ? "border-b border-line bg-ink/85 backdrop-blur-md"
                        : "border-b border-transparent bg-transparent"
                }`}
            >
                <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-5 lg:h-20 lg:px-10">
                    <Link
                        to="/"
                        data-testid="nav-logo-link"
                        onClick={(e) => {
                            if (pathname === "/") {
                                e.preventDefault();
                                scrollToTop();
                            }
                        }}
                        className="transition-opacity hover:opacity-80"
                    >
                        <Logo />
                    </Link>

                    <nav className="hidden items-center gap-7 xl:flex">
                        {LINKS.map((l) => {
                            const active = l.home
                                ? pathname === "/"
                                : pathname === l.to;
                            return (
                                <Link
                                    key={l.to}
                                    to={l.to}
                                    data-testid={`nav-link-${l.to === "/" ? "acasa" : l.to.slice(1)}`}
                                    onClick={(e) => {
                                        if (l.home && pathname === "/") {
                                            e.preventDefault();
                                            scrollToTop();
                                        }
                                    }}
                                    className={`group relative font-mono-tech text-[11px] tracking-[0.18em] transition-colors duration-300 ${
                                        active
                                            ? "text-flame"
                                            : "text-mist hover:text-paper"
                                    }`}
                                >
                                    {l.label}
                                    <span
                                        className={`absolute -bottom-1.5 left-0 h-px bg-flame transition-all duration-300 ${
                                            active
                                                ? "w-full"
                                                : "w-0 group-hover:w-full"
                                        }`}
                                    />
                                </Link>
                            );
                        })}
                    </nav>

                    <button
                        data-testid="menu-toggle-button"
                        aria-label="Meniu"
                        onClick={() => setOpen((v) => !v)}
                        className="relative flex h-11 w-11 flex-col items-center justify-center gap-[7px] border border-line bg-carbon/60 xl:hidden"
                    >
                        <span
                            className={`h-[2px] w-5 bg-paper transition-all duration-300 ${
                                open ? "translate-y-[4.5px] rotate-45" : ""
                            }`}
                        />
                        <span
                            className={`h-[2px] w-5 bg-paper transition-all duration-300 ${
                                open ? "-translate-y-[4.5px] -rotate-45" : ""
                            }`}
                        />
                    </button>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        className="fixed inset-0 z-40 flex flex-col justify-center bg-ink/[0.985] px-8 backdrop-blur-sm xl:hidden"
                    >
                        <nav className="flex flex-col gap-1">
                            {LINKS.map((l, i) => (
                                <motion.div
                                    key={l.to}
                                    initial={{ opacity: 0, x: -32 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -16 }}
                                    transition={{
                                        duration: 0.45,
                                        delay: 0.08 + i * 0.06,
                                        ease: [0.16, 1, 0.3, 1],
                                    }}
                                >
                                    <Link
                                        to={l.to}
                                        data-testid={`mobile-nav-link-${l.to === "/" ? "acasa" : l.to.slice(1)}`}
                                        onClick={() => setOpen(false)}
                                        className="group flex items-baseline gap-4 border-b border-line/60 py-4"
                                    >
                                        <span className="font-mono-tech text-xs text-flame">
                                            0{i + 1}
                                        </span>
                                        <span className="font-display text-3xl font-bold uppercase tracking-tight text-paper transition-colors group-hover:text-flame">
                                            {l.label}
                                        </span>
                                    </Link>
                                </motion.div>
                            ))}
                        </nav>
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.55 }}
                            className="mt-10 font-mono-tech text-[10px] tracking-[0.3em] text-mist"
                        >
                            © 2026 CAROTE.RO
                        </motion.p>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}